import { useEffect, useMemo, useState } from 'react';
import { usePreferencesStore } from '../../store/preferences';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useUiStore } from '../../store/ui';
import { PRIMARY_ENTRIES, PILOTAGE_ENTRIES, pathToTab, tabForPath } from '../../lib/navRegistry';
import type { NavEntry } from '../../lib/navRegistry';
import type { AppTabName } from '../../lib/types';
import { fetchAuthSession, fetchNavCounts, fetchProductionSettings, fetchVersionInfo, saveProductionSettings } from '../../lib/api';
import type { NavCountsPayload } from '../../lib/api';
import { safeString } from '../../lib/format';
import { Toaster } from '../ui/Toast';
import { CommandPalette } from '../ui/CommandPalette';
import { AiSidebar } from '../../features/ai/AiSidebar';
import { InternalDayCostMenuSetting } from '../../features/finance-ops/InternalDayCostSetting';
import { NotificationBell } from '../ui/NotificationBell';

/**
 * App-level H -> JH conversion, edited from the Système menu (default 6 h /
 * day). Every mission inherits it unless it carries its own « Heures par
 * jour » override in its drawer.
 */
function ProductionHoursSetting() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['settings', 'production'],
    queryFn: fetchProductionSettings,
    staleTime: 5 * 60 * 1000,
  });
  const [draft, setDraft] = useState('');
  const current = settingsQuery.data?.production_hours_per_day;
  const save = useMutation({
    mutationFn: saveProductionSettings,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings', 'production'] });
      queryClient.invalidateQueries({ queryKey: ['tab'] });
      setDraft('');
    },
  });
  const value = draft !== '' ? draft : current != null ? String(current) : '';
  return (
    <div className="shell__system-setting">
      <label>
        Heures / jour (JH)
        <input
          type="number"
          min="1"
          max="24"
          step="0.5"
          value={value}
          onChange={(event) => setDraft(event.target.value)}
          aria-label="Heures par jour pour la conversion en JH"
        />
      </label>
      <button
        type="button"
        className="button button--ghost"
        disabled={save.isPending || draft === '' || Number(draft) === current}
        onClick={() => save.mutate(Number(draft))}
      >
        {save.isPending ? '…' : 'OK'}
      </button>
      {save.isError ? <span className="shell__system-setting-error">Refusé</span> : null}
    </div>
  );
}

const ROLE_LABELS: Record<string, string> = { admin: 'Administrateur', manager: 'Gestionnaire', viewer: 'Lecture seule' };

function NavIcon({ path }: { path: string }) {
  return (
    <svg className="shell__nav-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

function NavItem({ item, activeTab, counts, onClick }: { item: NavEntry; activeTab: string; counts?: NavCountsPayload; onClick?: () => void }) {
  const count = item.badge && counts ? counts[item.badge.key] : null;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      className={({ isActive }) =>
        `shell__nav-link${isActive || activeTab === tabForPath(item.to) ? ' is-active' : ''}`
      }
    >
      <NavIcon path={item.svg} />
      <span className="shell__nav-label">{item.label}</span>
      {typeof count === 'number' && count > 0 ? (
        <span className="shell__nav-badge num" title={`${count} ${item.badge?.label}`}>
          <span aria-hidden="true">{count > 99 ? '99+' : count}</span>
          <span className="visually-hidden">{` · ${count} ${item.badge?.label}`}</span>
        </span>
      ) : null}
    </NavLink>
  );
}

function formatBuildIdentity(payload?: { env?: string | null; version?: string | null; sha?: string | null; buildDate?: string | null } | null): string {
  if (!payload) return 'Version inconnue';
  const env = safeString(payload.env) || 'local';
  const version = safeString(payload.version) || 'dev';
  const sha = safeString(payload.sha) ? safeString(payload.sha).slice(0, 8) : 'unknown';
  const buildDateRaw = safeString(payload.buildDate);
  const parsedDate = buildDateRaw ? new Date(buildDateRaw) : null;
  const buildDate = parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate.toLocaleDateString('fr-FR') : '';
  return [env, version, sha, buildDate].filter(Boolean).join(' · ');
}

function formatAuthIdentity(payload?: { auth_mode?: string | null; auth_source?: string | null; external_provider?: string | null } | null): string {
  if (!payload) return 'auth local';
  const mode = safeString(payload.auth_mode) || 'local';
  const source = safeString(payload.auth_source);
  const provider = safeString(payload.external_provider);
  return [mode, source || provider].filter(Boolean).join(' · ');
}

export function buildAvailableYears(selectedYear: number, serverYears: unknown = []): number[] {
  const currentYear = new Date().getFullYear();
  const values = new Set<number>();
  for (let year = Math.min(currentYear, selectedYear) - 2; year <= Math.max(currentYear, selectedYear) + 5; year += 1) {
    values.add(year);
  }
  if (Array.isArray(serverYears)) {
    serverYears.forEach((raw) => {
      const year = Number(raw);
      if (Number.isInteger(year) && year >= 2000 && year <= 2200) values.add(year);
    });
  }
  values.add(selectedYear);
  return [...values].sort((left, right) => left - right);
}

export function AppShell() {
  const location = useLocation();
  const selectedYear = useUiStore((state) => state.selectedYear);
  const setSelectedYear = useUiStore((state) => state.setSelectedYear);
  const activeTab = useUiStore((state) => state.activeTab);
  const setActiveTab = useUiStore((state) => state.setActiveTab);
  const compactMode = useUiStore((state) => state.compactMode);
  const setCompactMode = useUiStore((state) => state.setCompactMode);
  const theme = useUiStore((state) => state.theme);
  const setTheme = useUiStore((state) => state.setTheme);
  const todayLabel = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date());
  const versionQuery = useQuery({
    queryKey: ['version'],
    queryFn: fetchVersionInfo,
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const authSessionQuery = useQuery({
    queryKey: ['auth-session'],
    queryFn: fetchAuthSession,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const navCountsQuery = useQuery({
    queryKey: ['nav-counts'],
    queryFn: fetchNavCounts,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const availableYears = useMemo(() => buildAvailableYears(
    selectedYear,
    (versionQuery.data as Record<string, unknown> | undefined)?.available_years,
  ), [selectedYear, versionQuery.data]);

  useEffect(() => {
    setActiveTab(pathToTab(location.pathname || '/dashboard') as AppTabName);
  }, [location.pathname, setActiveTab]);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const user = authSessionQuery.data?.current_user as { email?: string | null; role?: string | null } | undefined;
  const userEmail = safeString(user?.email);
  // Column widths and other per-user interface choices follow the account.
  useEffect(() => {
    if (userEmail) void usePreferencesStore.getState().hydrate(userEmail);
  }, [userEmail]);
  const userRole = safeString(user?.role);
  const initials = (userEmail.split('@')[0] || '?')
    .split(/[._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || '?';

  return (
    <div className={`shell shell--sidebar${compactMode ? ' shell--compact' : ''}`}>
      <div className="shell__mobile-bar">
        <button
          type="button"
          className="shell__hamburger"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          aria-label="Menu de navigation"
          aria-expanded={mobileNavOpen}
        >
          {mobileNavOpen ? '✕' : '☰'}
        </button>
        <span className="shell__brand">AIRCS</span>
        <NotificationBell />
      </div>
      <aside className={`shell__sidebar${mobileNavOpen ? ' is-mobile-open' : ''}`}>
        <div className="shell__brand-lockup">
          <span className="shell__monogram" aria-hidden="true">A</span>
          <div className="shell__brand-text">
            <span className="shell__brand">AIRCS</span>
            <span className="shell__brand-sub">Business OS</span>
          </div>
          <div className="shell__brand-bell"><NotificationBell /></div>
        </div>
        <nav className="shell__nav" aria-label="Navigation principale">
          {PRIMARY_ENTRIES.map((item) => (
            <NavItem key={item.to} item={item} activeTab={activeTab} counts={navCountsQuery.data} />
          ))}
          <div className="shell__nav-group" role="group" aria-labelledby="shell-pilotage-label">
            <span id="shell-pilotage-label" className="shell__nav-group-label">Pilotage</span>
            {PILOTAGE_ENTRIES.map((item) => (
              <NavItem key={item.to} item={item} activeTab={activeTab} counts={navCountsQuery.data} />
            ))}
          </div>
        </nav>
        <div className="shell__sidebar-foot">
          <div className="shell__year-picker">
            <label htmlFor="year-select">Exercice</label>
            <select id="year-select" value={selectedYear} onChange={(event) => setSelectedYear(Number(event.target.value))}>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <details className="shell__system-status">
            <summary className="shell__account" aria-label="Compte et réglages">
              <span className="shell__avatar" aria-hidden="true">{initials}</span>
              <span className="shell__account-text">
                <span className="shell__account-name">{userEmail || 'Compte'}</span>
                <span className="shell__account-role">{ROLE_LABELS[userRole] || 'Réglages'}</span>
              </span>
              <span className={`shell__system-dot${versionQuery.data?.ok && authSessionQuery.data?.authenticated ? ' is-live' : ''}`} aria-hidden="true" />
              <span className="shell__system-trigger">Système</span>
            </summary>
            <div className="shell__system-popover" aria-label="Réglages et identité de la version">
              <div className="shell__system-row">
                <button
                  className="shell__header-link"
                  type="button"
                  aria-pressed={compactMode}
                  onClick={() => setCompactMode(!compactMode)}
                  aria-label={compactMode ? 'Mode actuel: compact. Basculer vers le mode confort.' : 'Mode actuel: confort. Basculer vers le mode compact.'}
                >
                  {compactMode ? 'Mode compact' : 'Mode confort'}
                </button>
                <button
                  className="shell__header-link"
                  type="button"
                  aria-pressed={theme === 'dark'}
                  onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                  aria-label={theme === 'light' ? 'Thème actuel: clair. Basculer vers le thème sombre.' : 'Thème actuel: sombre. Basculer vers le thème clair.'}
                >
                  {theme === 'light' ? 'Thème clair' : 'Thème sombre'}
                </button>
              </div>
              <ProductionHoursSetting />
              <InternalDayCostMenuSetting />
              <div className="shell__system-links">
                <Link className="shell__footer-link" to="/pack-comptable">Pack comptable</Link>
                <Link className="shell__footer-link" to="/ai">Assistant IA</Link>
                <Link className="shell__footer-link" to="/admin/audit">Journal d'audit</Link>
                <Link className="shell__footer-link" to="/confidentialite">RGPD & hébergement</Link>
              </div>
              <div className="shell__footer-left">
                <span className="shell__footer-build">
                  {versionQuery.isLoading ? 'Chargement de la version…' : formatBuildIdentity(versionQuery.data)}
                </span>
                <span className="shell__footer-date">{todayLabel}</span>
              </div>
              <div className="shell__system-identities">
                <span className={`shell__footer-pill${versionQuery.data?.ok ? ' is-live' : ''}`}>
                  {versionQuery.data?.slug || 'business-os'}
                </span>
                <span className={`shell__footer-pill${authSessionQuery.data?.authenticated ? ' is-live' : ''}`}>
                  {authSessionQuery.isLoading ? 'auth…' : formatAuthIdentity(authSessionQuery.data)}
                </span>
              </div>
              <a className="shell__header-link shell__header-link--logout" href="/logout">
                Déconnexion
              </a>
            </div>
          </details>
        </div>
      </aside>
      {mobileNavOpen ? (
        <button type="button" className="shell__scrim" aria-label="Fermer le menu" onClick={() => setMobileNavOpen(false)} />
      ) : null}
      <main className="shell__main">
        <Outlet />
      </main>
      <Toaster />
      <CommandPalette />
      <AiSidebar />
    </div>
  );
}
